import { Injectable } from '@jkhy/frontend-utils/di';
import { deepEquals } from '@jkhy/frontend-utils/functions';
import {
    AccountAccessModelDto,
    SaveUserAccountAccessModelDto,
    SaveUserAchLimitsRequestModelDto,
    SaveUserAchProductEntitlementsModelDto,
    SaveUserIpAccessRequestModelDto,
    SaveUserProductEntitlementsModelDto,
    SaveUserProductLimitsRequestModelDto,
    SearchUserModelDto,
} from '@treasury/api/channel';
import { UsersClient as UserClient } from '@treasury/api/channel/overrides';
import { LocalStorageService } from '@treasury/utils';
import {
    ProductFeatureEntitlementTypes,
    ProductFeatureLimitTypes,
} from '../../product-features/types';
import {
    CompanyUserInformation,
    CompanyUserProductFeatureSummary,
    CompanyUserState,
    CompanyUserSummary,
} from '../entities';
import {
    AccountAccess,
    CompanyUserAccountAccess,
    CompanyUserIpRestrictions,
    CompanyUserTimeAccess,
    IpRestrictionAddress,
} from '../entities/access';
import { CompanyUserReceivablesSettings } from '../entities/company-user-receivables-settings.entity';
import {
    CompanyUserAchEntitlements,
    CompanyUserProductEntitlements,
} from '../entities/entitlements';
import { CompanyUserAchLimits, CompanyUserProductLimits } from '../entities/limits';
@Injectable()
export class ConsumerCompanyUserHelper {
    constructor(
        private readonly userClient: UserClient,
        private readonly localStorageService: LocalStorageService
    ) {}

    /**
     * Retrieves the product feature summary for a specific user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @returns A promise that resolves to an instance of {@link UserProductFeatureSummary}
     *          containing the product feature summary for the specified user.
     * @throws Will throw an error if the request to fetch product feature summary fails.
     */
    public async getCompanyUserProductFeatureSummary(userId: number) {
        const response = await this.userClient.usersGetProductFeatureSummary(userId);
        return new CompanyUserProductFeatureSummary(response.data);
    }

    /**
     * Sends an enrollment email to a specific user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @returns A promise that resolves to the API response data after sending the enrollment email.
     * @throws Will throw an error if the request to send the enrollment email fails.
     */
    public sendEnrollmentEmail(userId: number) {
        this.userClient.usersGenerateEnrollmentEmail(userId);
    }

    /**
     * Retrieves the account access for a specific company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @returns A promise that resolves to an instance of {@link CompanyUserAccountAccess}
     *          containing the account access for the specified user.
     * @throws Will throw an error if the request to fetch account access fails.
     */
    public async getCompanyUserAccountAccess(userId: number) {
        const response = await this.userClient.usersGetUserAccountAccess(userId);
        return new CompanyUserAccountAccess(response.data);
    }

    /**
     * Retrieves the product limits for a specific company user by type.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param type - The type of product feature to retrieve limits for.
     * @returns A promise that resolves to an instance of {@link CompanyUserProductLimits}
     *          containing the product limits for the specified user.
     * @throws Will throw an error if the request to fetch product limits fails.
     */
    public async getCompanyUserProductLimitsByType(userId: number, type: ProductFeatureLimitTypes) {
        const response = await this.userClient.usersGetUserProductLimits(userId, {
            productFeatureType: type,
        });
        return new CompanyUserProductLimits(response.data);
    }

    /**
     * Retrieves the product entitlements for a specific company user by type.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param type - The type of product feature to retrieve entitlements for.
     * @returns A promise that resolves to an instance of {@link CompanyUserProductEntitlements}
     *          containing the product entitlements for the specified user.
     * @throws Will throw an error if the request to fetch product entitlements fails.
     */
    public async getCompanyUserProductEntitlementsByType(
        userId: number,
        type: ProductFeatureEntitlementTypes
    ) {
        const response = await this.userClient.usersGetUserProductEntitlements(userId, {
            productFeatureType: type,
        });
        return new CompanyUserProductEntitlements(response.data);
    }

    /**
     * Retrieves the ACH limits for a specific company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @returns A promise that resolves to an instance of {@link CompanyUserAchLimits}
     *          containing the ACH limits for the specified user.
     * @throws Will throw an error if the request to fetch ACH limits fails.
     */
    public async getCompanyUserAchLimits(userId: number) {
        const response = await this.userClient.usersGetUserAchLimits(userId);
        return new CompanyUserAchLimits(response.data);
    }

    /**
     * Retrieves the ACH entitlements for a specific company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @returns A promise that resolves to an instance of {@link CompanyUserAchEntitlements}
     *          containing the ACH entitlements for the specified user.
     * @throws Will throw an error if the request to fetch ACH entitlements fails.
     */
    public async getCompanyUserAchEntitlements(userId: number) {
        const response = await this.userClient.usersGetUserAchEntitlements(userId);
        return new CompanyUserAchEntitlements(response.data);
    }

    /**
     * Approves a company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param comments - The comments to include with the approval.
     * @returns A promise that resolves to the API response data after approving the user.
     */
    public async approveCompanyUser(userId: number, comments: string) {
        const response = await this.userClient.usersApprove(userId, {
            comments,
        });
        return response.data;
    }

    /**
     * Adds access to accounts for a specific company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param accounts - The accounts to add access to.
     * @returns A promise that resolves to the API response data after adding the access to the accounts.
     */
    public async addCompanyUserAccessToAccounts(userId: number, accounts: AccountAccess[]) {
        const body: SaveUserAccountAccessModelDto = {
            addedAccounts: accounts.map(a => a.toDto()) as AccountAccessModelDto[],
            removedAccounts: [],
        };

        const response = await this.userClient.usersSaveUserAccountAccess(userId, body);
        return response.data;
    }

    /**
     * Removes access to accounts for a specific company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param accounts - The accounts to add access to.
     * @returns A promise that resolves to the API response data after adding the access to the accounts.
     */
    public async removeCompanyUserAccessToAccounts(userId: number, accounts: AccountAccess[]) {
        const body: SaveUserAccountAccessModelDto = {
            addedAccounts: [],
            removedAccounts: accounts.map(a => a.toDto()) as AccountAccessModelDto[],
        };

        const response = await this.userClient.usersSaveUserAccountAccess(userId, body);
        return response.data;
    }

    /**
     * Rejects a company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param comments - The comments to include with the rejection.
     * @returns A promise that resolves to the API response data after rejecting the user.
     */
    public async rejectCompanyUser(userId: number, comments: string) {
        const response = await this.userClient.usersReject(userId, {
            comments,
        });
        return response.data;
    }

    /**
     * Retrieves IP Restriction details for a specific company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @returns A promise that resolves to an object containing a boolean of`hasPendingChanges`and {@link CompanyUserInformation}
     *          containing the user's basic information.
     * @throws Will throw an error if the request to fetch challenge details fails.
     */
    public async getCompanyUserInformation(userId: number) {
        const { pendingUser, user } = (await this.userClient.usersGetUserInformation(userId)).data;

        const hasPendingChanges = !deepEquals(pendingUser, user);

        return {
            hasPendingChanges,
            user: new CompanyUserInformation(hasPendingChanges ? pendingUser : user),
        };
    }

    /**
     * Retrieves the state information for a specific company user.
     *
     * @param {number} userId - The unique identifier of the user within the company.
     * @returns {Promise<CompanyUserState>} A promise that resolves to an instance of {@link CompanyUserState}
     *          containing the user's state information.
     * @throws {Error} Will throw an error if the request to fetch user state fails.
     */
    public async getCompanyUserState(userId: number): Promise<CompanyUserState> {
        const response = await this.userClient.usersGetState(userId);
        return new CompanyUserState(response.data);
    }

    /**
     * Deletes a company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @returns A promise that resolves to a boolean indicating whether the user was deleted.
     * @throws Will throw an error if the request to delete the user fails.
     */
    public async deleteCompanyUser(userId: number) {
        const response = await this.userClient.usersDeleteUser(userId);
        return response;
    }

    /**
     * Sets the active status of a company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param isActive - A boolean indicating whether the user should be active or not.
     * @param comments - Optional comments to include with the request.
     * @returns A promise that resolves to an instance of {@link CompanyUserSummary}
     *          containing the updated user information.
     * @throws Will throw an error if the request to set the user's active status fails.
     */
    public async setActive(userId: number, isActive: boolean, comments?: string) {
        const user = (
            await this.userClient.usersSetActive(userId, {
                isActive,
                comments,
            })
        ).data;
        return new CompanyUserSummary(user);
    }

    /**
     * Discards user information that is pending submission for a specific company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param comments - Comments to include with the approval.
     * @returns A promise that resolves to a boolean indicating whether the approval was successful.
     * @throws Will throw an error if the request to discard the user details fail.
     */
    public async discardCompanyUserChanges(userId: number, comments: string) {
        const response = await this.userClient.usersDiscard(userId, {
            comments: comments,
        });

        return response.data;
    }

    /**
     * Retrieves IP Restriction details for a specific company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @returns A promise that resolves to an instance of `CompanyUserIpRestrictions`
     *          containing the ip restriction details for the specified user.
     * @throws Will throw an error if the request to fetch challenge details fails.
     */
    public async getCompanyUserIpRestrictions(userId: number) {
        const response = await this.userClient.usersGetIpRestrictions(userId);
        return new CompanyUserIpRestrictions(response.data);
    }

    /**
     * Retrieves Time Access Restriction details for a specific company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @returns A promise that resolves to an instance of `CompanyUserTimeAccess`
     *          containing the ip restriction details for the specified user.
     * @throws Will throw an error if the request to fetch challenge details fails.
     */
    public async getCompanyUserTimeAccessRestrictions(userId: number) {
        const response = await this.userClient.usersGetUserTimeAccess(userId);
        return new CompanyUserTimeAccess(response.data);
    }

    /**
     *
     * @param userId - The unique identifier of the user within the company.
     * @param ipAddresses - The IP addresses to save.
     * @param restricted - Whether the IP addresses are restricted.
     * @returns A promise that resolves to the API response data after saving the IP restrictions.
     * @throws Will throw an error if the request to save IP restrictions fails.
     */
    public async saveCompanyUserIpRestrictions(
        userId: number,
        ipAddresses: IpRestrictionAddress[],
        restricted: boolean
    ) {
        const ipAddressDtos = ipAddresses.map(ria => ria.toDto());

        const response = await this.userClient.usersPutIpRestrictions(
            userId,
            { ipRestrictions: ipAddressDtos } as SaveUserIpAccessRequestModelDto,
            { restrict: restricted }
        );

        return response.data;
    }

    /**
     * Saves time access restrictions for a specific company user.
     *
     * This method updates the time-based access restrictions for a user within a company.
     * It converts the time access configuration to DTO format and sends it to the API.
     *
     * @param {number} userId - The unique identifier of the user within the company.
     * @param {CompanyUserTimeAccess} timeAccess - The time access restrictions configuration to save.
     * @returns {Promise<any>} A promise that resolves to the API response data after saving the time access restrictions.
     * @throws {Error} Will throw an error if the request to save time access restrictions fails.
     */
    public async saveCompanyUserTimeAccessRestrictions(
        userId: number,
        timeAccess: CompanyUserTimeAccess
    ) {
        const response = await this.userClient.usersSaveUserTimeAccess(userId, timeAccess.toDto());
        return response.data;
    }

    /**
     * Saves a user to the recently viewed users list for a given company.
     *
     * This method updates the list of recently viewed users for a specified company by adding
     * the provided user to the beginning of the list. If the user already exists in the list,
     * it is moved to the beginning. If the list exceeds 5 users, the oldest user is removed.
     *
     * @param companyId - The ID of the company.
     * @param user - The user to be added to the recently viewed users list.
     */
    public async saveUserToRecentlyViewedUser(userId: number) {
        const recentlyViewed = this.getRecentlyViewedCompanyUserIds()?.filter(id => id !== userId);
        if (!recentlyViewed || recentlyViewed.length < 1) {
            this.setRecentlyViewedCompanyUsers([userId]);
            return;
        }

        if (recentlyViewed) {
            if (recentlyViewed.length > 5) {
                recentlyViewed.pop();
            }
            recentlyViewed.unshift(userId);
            this.setRecentlyViewedCompanyUsers(recentlyViewed);
        }
    }

    /**
     * Retrieves the list of recently viewed users.
     *
     * @returns {number[]} An array of recently viewed user Ids.
     */
    public async getRecentlyViewedCompanyUsers() {
        const recentlyViewedIds = this.getRecentlyViewedCompanyUserIds();
        const users = await this.searchCompanyUsers({
            activeUsersOnly: false,
            enrolledUsersOnly: false,
            includeRoles: true,
        });
        return users.filter(u => {
            if (!u.id) {
                return false;
            }
            return recentlyViewedIds && recentlyViewedIds.includes(u.id);
        });
    }

    public async saveCompanyUserInformation(userId: number, user: CompanyUserInformation) {
        const response = await this.userClient.usersSaveUserInformation(userId, user.toDto());

        return response.data;
    }

    public async searchCompanyUsers(
        params: SearchUserModelDto = {
            activeUsersOnly: true,
            enrolledUsersOnly: true,
            includeRoles: true,
        }
    ): Promise<CompanyUserSummary[]> {
        const { activeUsersOnly, enrolledUsersOnly, includeRoles } = params;
        return (
            await this.userClient.usersSearch({
                activeUsersOnly,
                enrolledUsersOnly,
                includeRoles,
            })
        ).data.map(user => new CompanyUserSummary(user));
    }

    public async submitCompanyUser(userId: number) {
        const response = await this.userClient.usersSubmit(userId);
        return response.data;
    }

    public async saveCompanyUserProductLimits(userId: number, limits: CompanyUserProductLimits) {
        const response = await this.userClient.usersSaveUserProductLimits(
            userId,
            limits.toDto() as SaveUserProductLimitsRequestModelDto
        );
        return response.data;
    }

    public async saveCompanyUserProductAndAccountEntitlements(
        userId: number,
        entitlements: CompanyUserProductEntitlements
    ) {
        const addedUserClaims = entitlements.userProductClaims
            .filter(c => c.hasPendingUserClaim && !c.initialHasPendingUserClaim)
            .map(c => c.id);
        const removedUserClaims = entitlements.userProductClaims
            .filter(c => !c.hasPendingUserClaim && c.initialHasPendingUserClaim)
            .map(c => c.id);

        const addedUserClaimAccounts: Record<string, number[]> = {};
        const removedUserClaimAccounts: Record<string, number[]> = {};

        for (const account of entitlements.accounts ?? []) {
            for (const claim of account.userAccountClaims ?? []) {
                if (claim.hasPendingUserClaim && !claim.initialHasPendingUserClaim) {
                    const key = String(claim.id);
                    if (!addedUserClaimAccounts[key]) {
                        addedUserClaimAccounts[key] = [];
                    }
                    addedUserClaimAccounts[key].push(account.id ?? 0);
                } else if (!claim.hasPendingUserClaim && claim.initialHasPendingUserClaim) {
                    const key = String(claim.id);
                    if (!removedUserClaimAccounts[key]) {
                        removedUserClaimAccounts[key] = [];
                    }
                    removedUserClaimAccounts[key].push(account.id ?? 0);
                }
            }
        }

        const response = await this.userClient.usersSaveUserProductEntitlements(userId, {
            addedUserClaims,
            addedUserClaimAccounts,
            removedUserClaims,
            removedUserClaimAccounts,
        } as SaveUserProductEntitlementsModelDto);
        return response.data;
    }

    public async saveCompanyUserAchLimits(userId: number, limits: CompanyUserAchLimits) {
        const response = await this.userClient.usersSaveUserAchLimits(
            userId,
            limits.toDto() as SaveUserAchLimitsRequestModelDto
        );
        return response.data;
    }

    public async saveCompanyUserAchEntitlements(
        userId: number,
        entitlements: CompanyUserAchEntitlements
    ) {
        const addedEntitlements = entitlements.productClaims
            .filter(c => c.hasUserClaim && !c.hasPendingUserClaim)
            .map(c => ({ claim: c.toDto() }));
        const removedEntitlements = entitlements.productClaims
            .filter(c => !c.hasUserClaim && c.hasPendingUserClaim)
            .map(c => ({ claim: c.toDto() }));

        const response = await this.userClient.usersSaveUserAchProductEntitlements(userId, {
            addedEntitlements,
            removedEntitlements,
        } as SaveUserAchProductEntitlementsModelDto);
        return response.data;
    }

    public async getCompanyUserReceivablesSettings(userId: number) {
        const response = await this.userClient.usersGetUserReceivablesSettings(userId);
        return new CompanyUserReceivablesSettings(response.data ?? {});
    }

    public async saveCompanyUserReceivablesSettings(
        userId: number,
        settings: CompanyUserReceivablesSettings
    ) {
        const response = await this.userClient.usersPutUserReceivablesSettings(userId, {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            settings: settings.toDto() as any,
        });
        return response.data;
    }

    private getRecentlyViewedCompanyUserIds() {
        const recentlyViewed = this.localStorageService.get<number[]>('recentlyViewedUsers');
        return recentlyViewed ?? [];
    }

    private setRecentlyViewedCompanyUsers(users: number[]) {
        this.localStorageService.set('recentlyViewedUsers', [
            ...(this.localStorageService.get<number[]>('recentlyViewedUsers') ?? []),
            ...users,
        ]);
    }
}
